import type { Control } from 'react-hook-form';

import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import i18next from 'i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { Contact, ContactLanguage } from '~/api/clients/types';
import type { Quotation, SubmittedQuotation } from '~/api/quotations/types';

import {
  defaultQuotationQueryMetadata,
  quotationsQueryOptions,
  useCreateQuotationAttachment,
  useDeleteQuotationAttachment,
  useQuotationAttachment,
  useQuotationCalculationData,
  useUpdateDraftQuotation,
} from '~/api/quotations';
import { groupLinesInFormSections } from '~/features/documents';
import { useDebouncedState } from '~/hooks/useDebouncedState';
import {
  useDefaultBrandId,
  useDefaultClientAndContactId,
  useDefaultCondition,
  useDefaultCustomAgreement,
  useDefaultCustomCondition,
  useDefaultSpecialConditions,
} from '~/hooks/useDefaultFormValues';
import { useStableRef } from '~/hooks/useStableRef';
import { toast } from '~/utils/toast';

import type { CreateEditQuotationFormType } from './types';

import { mapQuotationFormDataToDraftPayload } from '../quotations.utils';

export const useQuotationDefaultValues = (quotation: Quotation): CreateEditQuotationFormType => {
  const brandId = useDefaultBrandId(quotation.brandId);
  const { clientId, contactId } = useDefaultClientAndContactId(quotation.clientId, quotation.contactId);
  const condition = useDefaultCondition(quotation.conditionId);
  const customCondition = useDefaultCustomCondition(quotation.customConditionId);
  const customAgreement = useDefaultCustomAgreement(quotation.customAgreementId, contactId);
  const specialConditions = useDefaultSpecialConditions(quotation.specialConditions, contactId, customAgreement?.id ?? null);

  const { t } = useTranslation(['quotations']);

  return {
    brandId,
    clientId,
    contactId,
    title: quotation.title ?? '',
    introduction: quotation.introduction ?? '',
    description: quotation.description ?? '',
    coEntryNumber: quotation.coEntryNumber ?? '',
    expirationDate: quotation.expirationDate ? dayjs(quotation.expirationDate).toDate() : null,
    condition: customAgreement ?? customCondition ?? condition,
    specialConditions,
    mail: quotation.mail || t('quotations:fields.mail.default'),
    sections: groupLinesInFormSections(quotation.quotationLines),
  };
};

/**
 * Save the draft quotation when input changes (debounced)
 */
export const useAutoSave = (id: number, control: Control<CreateEditQuotationFormType>, isEnabled = true) => {
  const { isPending, mutate: updateDraftQuotation } = useUpdateDraftQuotation(id);

  const data = useWatch({ control }) as CreateEditQuotationFormType;
  const [debouncedData] = useDebouncedState(data);

  useEffect(() => {
    if (!isEnabled) return;

    updateDraftQuotation(mapQuotationFormDataToDraftPayload(debouncedData));
  }, [debouncedData, isEnabled, updateDraftQuotation]);

  return isPending;
};

/**
 * Get the quotation calculation data based on the form data
 * (Because in edit mode, there is no auto save that will update the calculation data)
 */
export const useCalculationData = (quotation: Quotation, control: Control<CreateEditQuotationFormType>) => {
  const formData = useWatch({ control }) as CreateEditQuotationFormType;
  const [debouncedData] = useDebouncedState(formData, 100);

  const { data, isFetching } = useQuotationCalculationData(quotation, mapQuotationFormDataToDraftPayload(debouncedData));

  return useMemo(
    () => ({
      calculationData: data ?? quotation.calculationData, // Fallback in case the query fails
      isFetching,
    }),
    [data, quotation.calculationData, isFetching],
  );
};

export const useAttachment = (id: number) => {
  const { data: attachment } = useQuotationAttachment(id);
  const {
    isError: hasUploadFailed,
    isPending: isUploading,
    mutate: uploadAttachment,
    reset: resetUpload,
    variables: uploadedFile,
  } = useCreateQuotationAttachment(id);
  const { isPending: isRemoving, mutate: deleteAttachment } = useDeleteQuotationAttachment(id);

  const { t } = useTranslation(['quotations']);

  const select = useCallback(
    ([file]: File[]) => {
      if (file !== undefined) uploadAttachment(file);
    },
    [uploadAttachment],
  );

  const retry = useCallback(() => {
    if (uploadedFile !== undefined) uploadAttachment(uploadedFile);
  }, [uploadAttachment, uploadedFile]);

  const remove = useCallback(() => {
    // A failed upload never reached the server, so dropping the mutation is the whole removal.
    if (hasUploadFailed) return resetUpload();

    deleteAttachment(undefined, {
      // Drop the mutation too, so its file no longer describes the (now empty) attachment.
      onSuccess: () => resetUpload(),
      onError: () => toast.danger({ description: t('quotations:createEdit.attachments.error.delete') }),
    });
  }, [deleteAttachment, hasUploadFailed, resetUpload, t]);

  const open = useCallback(() => {
    if (!attachment) return;

    window.open(URL.createObjectURL(attachment), '_blank');
  }, [attachment]);

  /**
   * What there is to show, or `null` when there is nothing (the section renders the dropzone instead).
   *
   * `uploadedFile` is the selected file, held by the mutation from the moment the upload starts until
   * it is reset — which also covers the gap between a finished upload and the refetched attachment.
   */
  const file = useMemo(() => {
    if (!attachment && uploadedFile === undefined) return null;

    return {
      error: hasUploadFailed,
      isUploading,
      // The endpoint returns a bare blob, so the name is only known for a file uploaded in this session.
      name: uploadedFile?.name,
      size: uploadedFile?.size ?? attachment?.size,
    };
  }, [attachment, hasUploadFailed, isUploading, uploadedFile]);

  return useMemo(
    () => ({
      file,
      isRemoving,
      // Viewing needs the blob, which is absent while the upload is still in flight or has failed.
      open: attachment ? open : undefined,
      remove,
      retry,
      select,
    }),
    [attachment, file, isRemoving, open, remove, retry, select],
  );
};

/**
 * Get the mail content based on the contact's language
 */
export const useMailInContactLanguage = (contact: Contact | null, onMailLanguageChange: (mail: string) => void) => {
  const stableOnMailLanguageChange = useStableRef(onMailLanguageChange);

  // Keep track of this in state so it can be used to compare with the newly selected contact
  // Mail content should only be changed if the previous contact has a different language
  const [previousLanguage, setPreviousLanguage] = useState<ContactLanguage | null>(null);

  useEffect(() => {
    const newLanguage: ContactLanguage = contact?.language ?? 'nl';

    if (newLanguage !== previousLanguage) {
      const contactT = i18next.getFixedT(newLanguage);
      const mail = contactT('quotations:fields.mail.default');

      stableOnMailLanguageChange.current(mail);
      setPreviousLanguage(newLanguage);
    }
  }, [contact, previousLanguage, stableOnMailLanguageChange]);
};

/**
 * Get the entry number of the last submitted quotation
 *
 * Note: This hook accesses the query cache directly to avoid
 * subscribing to the cache and triggering unnecessary refetches.
 */
export const usePreviousEntryNumber = () => {
  const queryClient = useQueryClient();

  const queryData = queryClient.getQueryData(quotationsQueryOptions(defaultQuotationQueryMetadata).queryKey);

  const previousQuotation = (queryData?.data ?? []).filter(
    (quotation): quotation is SubmittedQuotation => quotation.status !== 'Draft' && dayjs(quotation.documentDate).isSame(dayjs(), 'year'),
  )[0];

  return previousQuotation?.coEntryNumber ?? null;
};
